/* eslint-disable eqeqeq */
import PropTypes from 'prop-types'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Button, Flex, Grid, GridItem, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Spinner, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { useAppContext } from 'hooks/useAppContext'
import { deleteProjectChangeOrder, getProjectChangeOrdersByProjectId } from 'api'
import { ModalProjectChangeOrderForm, ModalProjectChangeOrderFormType } from './ModalProjectChangeOrderForm'
import { ModalProjectChangeOrderApprove } from './ModalProjectChangeOrderApprove'
import { ModalProjectChangeOrderApproved } from './ModalProjectChangeOrderApproved'
import { getAssetUrl } from 'lib'
import { ModalPasswordConfirmation } from 'components/common'

export const ModalProjectChangeOrderList = ({ isOpen, onOpen, onClose, project }) => {
  const { createToast } = useAppContext()
  const [isLoading, setIsLoading] = useState(false)
  const [changeOrders, setChangeOrders] = useState([])
  const [selectedChangeOrder, setSelectedChangeOrder] = useState({
    type: ModalProjectChangeOrderFormType.Add,
    changeOrder: null
  })
  const disclosureForm = useDisclosure()
  const disclosureApprove = useDisclosure()
  const disclosureApproved = useDisclosure()
  const disclosureRemove = useDisclosure()

  const loadChangeOrders = async () => {
    if (!project?.id) return

    setIsLoading(true)
    try {
      const data = await getProjectChangeOrdersByProjectId(project.id)
      setChangeOrders(data)
      setIsLoading(false)
    } catch (err) {
      // createToast('error', 'Error', 'Failed to get change orders.')
      setChangeOrders([])
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadChangeOrders()
  }, [project, isOpen])

  const onAddChangeOrder = () => {
    setSelectedChangeOrder({
      type: ModalProjectChangeOrderFormType.Add,
      changeOrder: null
    })
    onClose()
    disclosureForm.onOpen()
  }

  const onClickApproved = (order) => {
    setSelectedChangeOrder({
      type: ModalProjectChangeOrderFormType.Approved,
      changeOrder: { ...order }
    })
    onClose()
    disclosureApproved.onOpen()
  }

  const onClickApprove = (order) => {
    setSelectedChangeOrder({
      type: ModalProjectChangeOrderFormType.Approve,
      changeOrder: { ...order }
    })
    onClose()
    disclosureApprove.onOpen()
  }

  const onClickDelete = (order) => {
    setSelectedChangeOrder({
      type: ModalProjectChangeOrderFormType.Delete,
      changeOrder: { ...order }
    })
    disclosureRemove.onOpen()
  }

  const handleRemove = async (id) => {
    try {
      await deleteProjectChangeOrder(id)
      disclosureRemove.onClose()
      loadChangeOrders()
      createToast('success', 'Success', 'Change order removed.')
    } catch {
      createToast('error', 'Error', 'Failed to remove the change order.')
    }
  }

  const onCloseChangeOrderForm = () => {
    disclosureForm.onClose()
    onOpen()
  }

  const onCloseChangeOrderApprove = () => {
    disclosureApprove.onClose()
    onOpen()
  }

  const onCloseChangeOrderApproved = () => {
    disclosureApproved.onClose()
    onOpen()
  }

  const onEditChangeOrder = (order) => {
    setSelectedChangeOrder({
      type: ModalProjectChangeOrderFormType.Edit,
      changeOrder: { ...order }
    })
    disclosureApprove.onClose()
    disclosureForm.onOpen()
  }

  const nextChangeOrderName = useMemo(() => {
    let nextNo = 0
    changeOrders.forEach(order => {
      const numberAfterR = order.name.match(/r(\d+)/)
      if (numberAfterR) {
        const extractedNumber = parseInt(numberAfterR[1])
        if (extractedNumber > nextNo) {
          nextNo = extractedNumber
        }
      }
    })
    return `#${project?.quoteNumber}r${nextNo + 1}`
  }, [changeOrders])

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading size="md">Change Order(s) for Quote #{project?.quoteNumber}</Heading>
            <Text fontSize="sm" mt={2}>{project?.customerName} at {project?.customerCompany}</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading ? (
              <Flex alignItems="center" gap={4}>
                <Spinner color="blue.400" size="sm" />
                <Text fontSize="sm">Loading ...</Text>
              </Flex>
            ) : (
              <VStack alignItems="none" gap={4}>
                {!changeOrders.length && (
                  <Text fontSize="sm">No change orders.</Text>
                )}
                <Grid templateColumns="repeat(8, 1fr)" gap={4}>
                  {changeOrders.map(order => {
                    return (
                      <Fragment key={order.id}>
                        <GridItem colSpan={2}>
                          <Text fontSize="sm">{order.name}</Text>
                        </GridItem>
                        <GridItem colSpan={3}>
                          <Text fontSize="sm">by {order.isApproved == true ? order.approvedUser : order.createdUser}</Text>
                        </GridItem>
                        <GridItem colSpan={3}>
                          {order.isApproved == true ? (
                            <Flex gap={2}>
                              <Button size="sm" onClick={() => window.open(getAssetUrl(order.signedDoc))}>Signed Doc</Button>
                              <Button size="sm" colorScheme="blue" onClick={() => onClickApproved(order)}>Approved</Button>
                              <Button size="sm" colorScheme="red" onClick={() => onClickDelete(order)}>Delete</Button>
                            </Flex>
                          ) : (
                            <Flex gap={2}>
                              <Button size="sm" onClick={() => order.pdf && window.open(getAssetUrl(order.pdf))}>View</Button>
                              <Button size="sm" colorScheme="blue" onClick={() => onClickApprove(order)}>Approve</Button>
                              <Button size="sm" colorScheme="red" onClick={() => onClickDelete(order)}>Delete</Button>
                            </Flex>
                          )}
                        </GridItem>
                      </Fragment>
                    )
                  })}
                </Grid>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onAddChangeOrder}>
              Add Change Order
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ModalProjectChangeOrderForm
        isOpen={disclosureForm.isOpen}
        onClose={onCloseChangeOrderForm}
        type={selectedChangeOrder.type}
        changeOrder={selectedChangeOrder.changeOrder}
        nextChangeOrderName={nextChangeOrderName}
        project={project}
      />
      <ModalProjectChangeOrderApprove
        isOpen={disclosureApprove.isOpen}
        onClose={onCloseChangeOrderApprove}
        onEdit={onEditChangeOrder}
        type={ModalProjectChangeOrderFormType.Approve}
        changeOrder={selectedChangeOrder.changeOrder}
      />
      <ModalProjectChangeOrderApproved
        isOpen={disclosureApproved.isOpen}
        onClose={onCloseChangeOrderApproved}
        changeOrder={selectedChangeOrder.changeOrder}
      />
      {disclosureRemove.isOpen && (
        <ModalPasswordConfirmation
          heading="Are you sure to remove the change order?"
          btnConfirmLabel="Remove"
          onConfirm={() => handleRemove(selectedChangeOrder.changeOrder?.id)}
          {...disclosureRemove}
        />
      )}
    </>
  )
}

ModalProjectChangeOrderList.propTypes = {
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  project: PropTypes.object
}
